<script lang="ts">
	import { format } from 'svelte-i18n';
	import { scenes } from 'src/stores/lists/scenes';
	import DoPreview from 'src/components/Scenes/SceneDrawSections/DoPreview.svelte';
	import ElsePreview from '../Scenes/SceneDrawSections/ElsePreview.svelte';
	import WhenPreview from 'src/components/Scenes/SceneDrawSections/WhenPreview.svelte';
	import ActiveTimePreview from 'src/components/Scenes/SceneDrawSections/ActiveTimePreview.svelte';
	import {
		Modal,
		Button,
		DrawerTabs,
		DrawerTab,
		DrawerTabContent,
		Text,
		IconButton,
		ControlsContainer,
		ParameterCard,
		Container,
		toast,
		Alert,
		Icon,
		Tooltip,
	} from '@Shelly/shelly-design-system';
	import SceneWizard from 'src/components/Scenes/SceneWizard/SceneWizard.svelte';
	import SceneSection from 'src/components/Scenes/SceneDrawSections/SceneSection.svelte';
	import MorePreview from 'src/components/Scenes/SceneDrawSections/MorePreview.svelte';
	import NotificationRecipientsPreview from 'src/components/Scenes/SceneDrawSections/NotificationRecipientsPreview.svelte';
	import { summariseSceneNotificationRecipients } from 'src/components/Scenes/helpers/notification_recipients_helpers';
	import ControlSceneEnable from 'src/components/Controls/Scene/ControlSceneEnable.svelte';
	import ControlScenePlay from 'src/components/Controls/Scene/ControlScenePlay.svelte';
	import { copy } from 'src/helpers/copy_text';
	import { _calculate_duration, _deepClone, capFirstChar } from 'src/helpers/utilities';
	import type { ISceneObj } from 'src/types/SceneType';
	import { calculateWhenEnd, load_image_from } from 'src/helpers/interface_helpers';
	import { onMount } from 'svelte';
	import { mobile } from 'src/services/mobile-rework';
	import WhenStep from 'src/components/Scenes/SceneWizard/Steps/WhenStep.svelte';
	import DoStep from 'src/components/Scenes/SceneWizard/Steps/DoStep.svelte';
	import { user_data } from 'src/stores/account/user';
	import { rooms } from 'src/stores/lists/rooms';
	import { edit } from 'src/stores/ui/edit_lists';
	import { getDuplicateInfo, generateCopyName } from 'src/components/Scenes/helpers/sceneUtils';
	import { postReq } from 'src/services/http';
	import {
		check_for_broken_conditions_or_actions,
		validate_scene,
	} from 'src/components/Scenes/helpers/validators';
	import { scene_object_prototype } from 'src/components/Scenes/SceneWizard/scene_object';
	import { isFlow as isFlowScene } from 'src/components/Lists/list-helpers';
	import { openDrawer } from 'src/helpers/drawer_helpers';
	import { location, push } from 'svelte-spa-router';
	import ActiveTimeStep from 'src/components/Scenes/SceneWizard/Steps/ActiveTimeStep.svelte';
	import ScriptsHighlight from 'src/components/DevicesSettings/Scripts/Components/ScriptsHighlight.svelte';
	import type { AvatarColorVariantTypes } from '@Shelly/shelly-design-system';
	import { isDevServerUser, isT1User } from 'src/helpers/user_server_conditions';
	import { addRemoveDeviceIdsFromMetaInSceneObject } from 'src/components/Scenes/SceneWizard/Process/GenericComponents/helpers';
	import { isSubAccount } from 'src/helpers/user_helpers';
	import { canManageHomeEntities, isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { getSharedHomeManagedTooltip } from 'src/helpers/houses/shared_home_messaging_helpers';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';
	import { homes } from 'src/stores/account/home';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { isExperimentalEnabled } from 'src/helpers/user_settings_helpers';
	import AddEditInterval from '../Scenes/SceneWizard/Process/ActiveTime/AddEditInterval.svelte';
	import { ScriptSceneEditor, ScriptSceneSection, type ScriptScene } from '@Shelly/scenes-script';
	import ShellyScriptEditor from '../DevicesSettings/Scripts/Editor/ShellyScriptEditor';
	import { normalizeSceneDoStatements } from 'src/components/Flows/converters/flow-island-utils';

	let id = $sidePanelStateCombined?.id;
	let showAddEditCondition = false;
	let when_info = { mode: 'add', type: 'or', main_ind: -1, sub_ind: -1 };
	let element;
	let showAddEditAction = false;
	let do_info = { mode: 'add', type: 'action', ind: -1 };
	let showEditActiveTime = false;
	let editedInterval;

	let scene: ISceneObj | undefined = $scenes[id] ? (_deepClone($scenes[id]) as ISceneObj) : undefined;

	$: if (
		$scenes[id] &&
		!showAddEditModal &&
		!showAddEditCondition &&
		!showAddEditAction &&
		!showEditActiveTime
	) {
		scene = _deepClone($scenes[id]) as ISceneObj;
	}

	$: name = scene?._meta?.name || '';
	$: run_on_save = scene?._run_on_ingest || false;
	$: scene_enable_val = scene?._enabled || false;
	$: allowed_for_3rd_party = scene?._meta?.integrations || false;

	// WhenPreview expects each when to have .and array; Flow produces flat scene.if.or
	$: rawWhens =
		scene?.if?.or ||
		scene?.if?.and?.find((el) => el?.or && el.or?.every((e) => !e.indailyint))?.or ||
		[];
	$: whens = rawWhens.map((w) =>
		w && typeof w === 'object' && Array.isArray(w.and) ? w : { and: [w] },
	);
	$: thens = normalizeSceneDoStatements(scene?.do);
	$: elses = scene?.else || [];
	$: active_time = scene?.if?.and?.find((el) => el && el.indailyint)
		? [scene?.if?.and?.find((el) => el && el.indailyint)]
		: scene?.if?.and?.find((el) => el.or && el?.or?.every((e) => e.indailyint))
			? scene?.if?.and?.find((el) => el?.or?.every((e) => e.indailyint))?.or
			: [
					{
						indailyint: { dows: '1 2 3 4 5 6 7', startat: '00:00', dur: 1439 },
					},
				];

	$: duplicateInfo = scene ? getDuplicateInfo(scene, $scenes) : null;

	/**
	 * FE-1682: Only render the recipients section when the scene has at least one
	 * push or mail notification action; otherwise the section would be empty and noisy.
	 */
	$: notificationSummary =
		typeof scene === 'object' ? summariseSceneNotificationRecipients(scene) : {};
	$: hasNotificationRecipients = !!notificationSummary.push || !!notificationSummary.mail;

	let showAddEditModal = false;
	let showSharedManagedModal = false;

	// Reset edit state when modal is closed (including when closed by SceneWizard)
	$: if (!showAddEditModal && $edit) {
		edit.set(false);
	}

	$: sections =
		typeof scene === 'object'
			? [
					{
						slot_component: WhenPreview,

						slot_props: { props: { whens, slot: 'content' } },
						props: { text: $format('title_when'), _class: 'mb-s' },
					},
					{
						slot_component: DoPreview,

						slot_props: { props: { thens, slot: 'content' } },
						props: { text: $format('title_do'), _class: 'mb-s mt-m' },
					},
					...(elses.length > 0
						? [
								{
									slot_component: ElsePreview,
									slot_props: { props: { elses, slot: 'content' } },
									props: { text: 'Else', _class: 'mb-s mt-m' },
								},
							]
						: []),
					{
						slot_component: ActiveTimePreview,

						slot_props: {
							props: { slot: 'content', active_time },
						},
						props: {
							text: $format('title_active_time'),
							_class: 'mb-s mt-m',
						},
					},
					...(hasNotificationRecipients
						? [
								{
									slot_component: NotificationRecipientsPreview,
									// cast: keeps the broader `slot_props.props` union compatible
									// with the strictly-typed `scene` ref above (other entries
									// don't carry a `scene` field).
									slot_props: { props: { slot: 'content', scene } as any },
									props: {
										text: $format('scene_notification_recipient_section_title', {
											default: 'Notification recipients',
										}),
										_class: 'mb-s mt-m',
									},
								},
							]
						: []),
					{
						slot_component: MorePreview,
						slot_props: {
							props: {
								slot: 'content',
								run_on_save,
								allowed_for_3rd_party,
								scene_enable_val,
							},
						},
						props: { text: $format('title_more'), _class: 'mb-s mt-m' },
					},
				]
			: [];

	$: scriptTab = isExperimentalEnabled('inf2')
		? [
				{
					icon: 'fa-solid fa-code-simple',
				},
			]
		: [];
	$: drawerTabs =
		isExperimentalEnabled('inf2') && scene?.infinno
			? [
					{
						icon: 'fa-solid fa-code-simple',
					},
				]
			: typeof scene === 'object'
				? [
						{
							image: scene?._meta?.image
								? load_image_from(scene?._meta?.image)
								: undefined,
							backgroundColor: scene?._meta?.backgroundColor as AvatarColorVariantTypes,
						},
						{
							icon: 'fa-light fa-play',
						},
						{
							icon: 'fal fa-info-circle',
						},
						...scriptTab,
					]
				: [{ icon: 'fal fa-trash' }];
	let activeTab = 0;
	let showScriptEditorModal = false;

	const handleSceneEdit = () => {
		if (isActiveHomeShared($homes.list)) {
			showSharedManagedModal = true;
			return;
		}
		if (scene?.infinno) {
			showScriptEditorModal = true;
		} else if (scene?._meta?.flowEdited) {
			// Flow-edited scenes open in Flow Editor, not SceneWizard
			push(`/home/flows/${scene._id}/edit`);
		} else {
			scene = _deepClone($scenes[scene._id]) as ISceneObj;
			showAddEditModal = true;
		}
	};

	const handleDuplicateScene = () => {
		showDuplicateConfirmModal = true;
	};

	const confirmDuplicateScene = async () => {
		if (isDuplicating) return; // Prevent double-clicks

		isDuplicating = true;

		try {
			// Create deep copy of the original scene
			const originalScene = $scenes[id];
			if (!originalScene) {
				throw new Error('Original scene not found');
			}

			const duplicatedScene = createSceneDuplicate(originalScene);

			// Validate the duplicated scene
			if (!validate_scene(duplicatedScene)) {
				throw new Error('Duplicated scene validation failed');
			}

			// Check for conflicts if the duplicated scene would be active
			const conflicts = checkSceneConflicts(duplicatedScene);

			// Save the duplicated scene
			const savedScene = await save_scene(duplicatedScene);
			if (!savedScene || !savedScene._id) {
				throw new Error('Failed to save duplicated scene');
			}

			scenes.addScene(savedScene);

			// Store the ID for post-duplicate modal
			duplicatedSceneId = savedScene._id;

			// Show conflicts information if any
			if (conflicts.hasConflicts && duplicatedScene._enabled) {
				console.log('Scene conflicts detected:', conflicts);
				// TODO: Show conflict notification in the post-duplicate modal - this may need some thinking
			}

			showDuplicateConfirmModal = false;

			// Show success toast notification
			toast.open({
				type: 'success',
				title: $format('success_duplicate_scene', {
					default: 'Scene duplicated successfully',
				}),
			});
			showPostDuplicateModal = true;
		} catch (error) {
			console.error('Failed to duplicate scene:', error);
			showDuplicateConfirmModal = false;

			// Show error toast notification
			toast.open({
				type: 'danger',
				title: $format('error_duplicate_scene', {
					default: 'Failed to duplicate scene',
				}),
			});
		} finally {
			isDuplicating = false;
		}
	};

	const createSceneDuplicate = (originalScene: ISceneObj): ISceneObj => {
		const duplicate = _deepClone(originalScene) as ISceneObj;

		// Clear backend-generated fields
		delete duplicate._id;
		delete duplicate._name;
		delete duplicate.id;
		delete duplicate._file_modtag;

		// Modify metadata according to requirements
		duplicate._meta = {
			...duplicate._meta,
			name: generateCopyName(originalScene._meta.name, $scenes),
			room: originalScene._meta.room, // Preserve room
			roomn: '', // Clear room name
			position: -1,
			// Keep the original image (requirement #1)
			// image: originalScene._meta.image, // Already preserved in clone
			// Preserve cdi and adi arrays (requirement #2)
			cdi: originalScene._meta.cdi ? [...originalScene._meta.cdi] : undefined,
			adi: originalScene._meta.adi ? [...originalScene._meta.adi] : undefined,
			sceneType: isFlowScene(originalScene) ? 'flow' : (originalScene._meta.sceneType ?? 'basic'),
			// Clear other metadata as needed
			extra: undefined,
			broken: undefined,
		};

		// Scene activation logic
		// If original scene is activated, new scene should NOT be activated
		if (originalScene._enabled) {
			duplicate._enabled = false;
		}
		// If original scene is not activated, keep the same state (false)

		// Do not run the scene automatically
		duplicate._run_on_ingest = false;

		return duplicate;
	};

	const handleEditDuplicatedScene = () => {
		showPostDuplicateModal = false;
		scene = _deepClone($scenes[duplicatedSceneId]) as ISceneObj;
		id = duplicatedSceneId;
		edit.set(true);
		showAddEditModal = true;
	};

	/**
	 * Check for scene conflicts and device usage
	 */
	const checkSceneConflicts = (sceneToCheck: ISceneObj) => {
		const allScenes = Object.values($scenes);
		const conflicts = {
			hasConflicts: false,
			similarActiveScenes: [],
			deviceUsage: {
				conditions: [],
				actions: [],
			},
		};

		// Check for active scenes with similar flow
		const activeScenesWithSimilarFlow = allScenes.filter((scene) => {
			if (!scene._enabled || scene._id === sceneToCheck._id) return false;

			// Compare if scenes have similar trigger conditions
			return hasSimilarFlow(scene, sceneToCheck);
		});

		if (activeScenesWithSimilarFlow.length > 0) {
			conflicts.hasConflicts = true;
			conflicts.similarActiveScenes = activeScenesWithSimilarFlow.map((scene) => ({
				id: scene._id,
				name: scene._meta.name,
				room: scene._meta.roomn || 'General',
			}));
		}

		// Check device usage in conditions and actions
		conflicts.deviceUsage = checkDeviceUsage(sceneToCheck);

		return conflicts;
	};

	/**
	 * Check if two scenes have similar flow (similar trigger conditions)
	 */
	const hasSimilarFlow = (scene1: ISceneObj, scene2: ISceneObj): boolean => {
		// Extract device IDs from conditions
		const scene1Devices = extractDeviceIdsFromConditions(scene1);
		const scene2Devices = extractDeviceIdsFromConditions(scene2);

		// Check if they share any device IDs in conditions and return result
		return scene1Devices.some((deviceId) => scene2Devices.includes(deviceId));
	};

	/**
	 * Extract device IDs from scene conditions
	 */
	const extractDeviceIdsFromConditions = (scene: ISceneObj): string[] => {
		const deviceIds = [];
		const conditions = scene?.if?.or || [];

		conditions.forEach((condition) => {
			if (condition.device_id) {
				deviceIds.push(condition.device_id);
			}
			if (condition.and) {
				condition.and.forEach((subCondition) => {
					if (subCondition.device_id) {
						deviceIds.push(subCondition.device_id);
					}
				});
			}
		});

		return [...new Set(deviceIds)]; // Remove duplicates
	};

	/**
	 * Check device usage across all scenes
	 */
	const checkDeviceUsage = (sceneToCheck: ISceneObj) => {
		const allScenes = Object.values($scenes);
		const usage = {
			conditions: [],
			actions: [],
		};

		// Extract devices from the scene to check
		const sceneDevicesInConditions = extractDeviceIdsFromConditions(sceneToCheck);
		const sceneDevicesInActions = extractDeviceIdsFromActions(sceneToCheck);

		// Check usage in other scenes
		allScenes.forEach((scene) => {
			if (scene._id === sceneToCheck._id) return;

			const otherSceneConditionDevices = extractDeviceIdsFromConditions(scene);
			const otherSceneActionDevices = extractDeviceIdsFromActions(scene);

			// Check condition device overlaps
			sceneDevicesInConditions.forEach((deviceId) => {
				if (
					otherSceneConditionDevices.includes(deviceId) ||
					otherSceneActionDevices.includes(deviceId)
				) {
					usage.conditions.push({
						deviceId,
						usedInScene: {
							id: scene._id,
							name: scene._meta.name,
							enabled: scene._enabled,
						},
					});
				}
			});

			// Check action device overlaps
			sceneDevicesInActions.forEach((deviceId) => {
				if (
					otherSceneConditionDevices.includes(deviceId) ||
					otherSceneActionDevices.includes(deviceId)
				) {
					usage.actions.push({
						deviceId,
						usedInScene: {
							id: scene._id,
							name: scene._meta.name,
							enabled: scene._enabled,
						},
					});
				}
			});
		});

		return usage;
	};

	/**
	 * Extract device IDs from scene actions
	 */
	const extractDeviceIdsFromActions = (scene: ISceneObj): string[] => {
		const deviceIds = [];
		const actions = normalizeSceneDoStatements(scene?.do);

		actions.forEach((action) => {
			if (action.device_id) {
				deviceIds.push(action.device_id);
			}
			if (action.blk) {
				action.blk.forEach((block) => {
					if (block.device_id) {
						deviceIds.push(block.device_id);
					}
				});
			}
		});

		return [...new Set(deviceIds)]; // Remove duplicates
	};

	const closeModal = (e: CustomEvent) => {
		if (showAddEditModal) {
			e.preventDefault();
			showAddEditModal = false;
		}
	};

	const openEditConditionWizard = (e) => {
		showAddEditCondition = true;
		when_info = {
			main_ind: e.detail.main_ind,
			mode: 'edit',
			sub_ind: e.detail.sub_ind,
			type: 'and',
		};
		element =
			when_info.main_ind >= 0 && when_info.sub_ind >= 0
				? whens[when_info.main_ind]['and'][when_info.sub_ind]
				: undefined;

		scene = addRemoveDeviceIdsFromMetaInSceneObject('condition', element, scene);
	};

	const openEditActionWizard = (e) => {
		showAddEditAction = true;
		do_info = { mode: 'edit', type: 'action', ind: e.detail };
		element = do_info.ind >= 0 ? thens[do_info.ind] : undefined;

		scene = addRemoveDeviceIdsFromMetaInSceneObject('action', element, scene);
	};

	const openEditActiveTime = (e) => {
		showEditActiveTime = true;
		editedInterval = {
			ind: e.detail.ind,
			active_time: e.detail,
		};
	};

	const save_scene = async (scene) => {
		scene._meta.room = scene._meta.room == '' ? -1 : scene._meta.room;
		scene._meta.roomn =
			typeof $rooms[scene._meta.room] !== 'undefined'
				? $rooms[scene._meta.room].name
				: scene._meta.room == -1
					? 'General'
					: 'Missing';

		scene._meta.name = scene._meta.name.trim();
		scene._meta.sceneType = isFlowScene(scene) ? 'flow' : 'basic';

		if (
			!$scenes[scene?._id] ||
			Number($scenes[scene?._id]?._meta?.room) !== Number(scene?._meta.room)
		) {
			scene._meta.position =
				Object.values($scenes).filter(
					(sc) =>
						!sc.tschem &&
						sc._gui_function !== 'alarm' &&
						(sc._meta?.sceneType ?? 'basic') === (scene._meta?.sceneType ?? 'basic') &&
						Number(sc._meta?.room) === Number(scene._meta?.room),
				).length || 0;
		}

		if (
			typeof scene.name !== 'undefined' ||
			typeof scene.position !== 'undefined' ||
			typeof scene.room !== 'undefined' ||
			typeof scene.id !== 'undefined' ||
			typeof scene.image !== 'undefined' ||
			typeof scene.backgroundColor !== 'undefined'
		) {
			delete scene.position;
			delete scene.id;
			delete scene.room;
			delete scene.name;
			delete scene.image;
			delete scene.backgroundColor;
		}

		let res = {};

		if (scene._id === undefined && !$edit) {
			//we save new scene
			res = await postReq('/scene/add', {
				scene_script: JSON.stringify(scene),
			});
			if (res.isok && res?.data?.scene_id) {
				scene._id = res.data.scene_id;
				return scene;
			}
		}

		if (scene._id) {
			//we edit a scene
			res = await postReq('/scene/edit', {
				id: scene._id,
				scene_script: JSON.stringify(scene),
			});
			if (res.isok && res?.data?.scene_id) {
				return scene;
			}
		}
	};

	const check_broken = () => {
		if (scene._meta.broken) {
			const isBroken = check_for_broken_conditions_or_actions(scene);
			if (isBroken) {
				return true;
			} else {
				scene._meta.broken = false;
				return false;
			}
		}
	};

	const handleSave = () => {
		if (validate_scene(scene) && !check_broken()) {
			save_scene(scene)
				.then((scene) => {
					if (!$edit) {
						scenes.addScene(scene);
					} else if ($edit) {
						scenes.editScene(scene);
					}

					if ($scenes[scene._id]) {
						let room = $scenes[scene._id]._meta.room;
					}
					openDrawer('scene', scene?._id);

					// Reset edit state and scene object
					edit.set(false);
					scene = _deepClone(scene_object_prototype) as ISceneObj;
				})
				.catch((err) => {
					console.log(err);
				});
		} else {
			console.log('return false');
			// process_request = false;
		}
	};

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('back_button', closeModal);
			return () => mobile.removeEventListener('back_button', closeModal);
		}
	});

	let showCodePreviewModal = false;
	let showDuplicateConfirmModal = false;
	let showPostDuplicateModal = false;
	let duplicatedSceneId = '';
	let isDuplicating = false;
	let isMobile = mobile.isMobile;
	let isFlow = $location.includes('flow');
	let disableEditButton = isMobile && isFlow;

	const onCloseCodePreviewModal = () => {
		showCodePreviewModal = false;
	};

	const onCloseDuplicateConfirmModal = () => {
		showDuplicateConfirmModal = false;
	};

	const onClosePostDuplicateModal = () => {
		showPostDuplicateModal = false;
		openDrawer('scene', duplicatedSceneId);
		duplicatedSceneId = '';
	};

	function onSceneConvertedToScript(e: CustomEvent<ScriptScene>) {
		scene.infinno = e.detail;
		activeTab = 0;
	}

	function onScriptEditorClose() {
		showScriptEditorModal = false;
	}
</script>

<DrawerTabs withScrollable={activeTab !== 0} backgroundImgSrc="">
	<svelte:fragment slot="tabs">
		{#key activeTab}
			{#each drawerTabs as drawerTab, ind}
				<DrawerTab
					active={activeTab === ind}
					image={drawerTab.image}
					backgroundColor={drawerTab.backgroundColor}
					icon={drawerTab.icon
						? activeTab === ind
							? `fa-solid ${drawerTab.icon} text-primary`
							: drawerTab.icon
						: undefined}
					on:click={() => (activeTab = ind)}
				/>
			{/each}
		{/key}
	</svelte:fragment>
	{#key activeTab}
		{#if typeof scene === 'object'}
			<DrawerTabContent heading={name} withScrollable={activeTab === 0}>
				<!-- Duplicate Scene Alert -->
				{#if duplicateInfo}
					<Alert
						type="info"
						class="mb-m"
						heading={$format('scene_duplicate_alert_heading', {
							default: 'Duplicate Scene Detected',
						})}
						message={$format('scene_duplicate_alert_title', {
							values: { original_name: duplicateInfo.originalName },
							default: `Scene is identical to "${duplicateInfo.originalName}"`,
						})}
					>
						{$format('scene_duplicate_alert_message', {
							default: `This scene has the same conditions and actions as the original.`,
						})}
					</Alert>
				{/if}

				<div slot="headingButton" class="d-flex">
					{#if !isSubAccount()}
						{#if isActiveHomeShared($homes.list)}
							<Tooltip placement="top" title={getSharedHomeManagedTooltip('edit_entity')}>
								<IconButton
									aria-label={$format('button_duplicate_scene', {
										default: 'Duplicate scene',
									})}
									icon="fas fa-clone"
									class="mr-xs"
									disabled
								/>
							</Tooltip>
						{:else}
							<IconButton
								aria-label={$format('button_duplicate_scene', {
									default: 'Duplicate scene',
								})}
								icon="fas fa-clone"
								class="mr-xs"
								disabled={disableEditButton}
								on:click={handleDuplicateScene}
							/>
						{/if}
					{/if}
					{#if !isSubAccount()}
						<IconButton
							aria-label={$format('prompt_title_edit_scene', { default: 'Edit scene' })}
							slot="headingButton"
							icon="fas fa-pen"
							disabled={disableEditButton}
							on:click={handleSceneEdit}
						/>
					{/if}
				</div>
				{#if isExperimentalEnabled('inf2') && scene?.infinno}
					<ScriptSceneSection
						user_data={$user_data}
						{scene}
						activeTimeComponent={ActiveTimePreview}
						morePreviewComponent={MorePreview}
						on:sceneConvertedToScript={(e) => onSceneConvertedToScript(e)}
					/>
				{:else if activeTab === 0}
					{#key sections}
						<div>
							{#each sections as section}
								<SceneSection {...section.props}>
									<svelte:component
										this={section.slot_component}
										on:edit-when={(e) => openEditConditionWizard(e)}
										on:edit-do={(e) => openEditActionWizard(e)}
										on:edit-active-time={(e) => {
											openEditActiveTime(e);
										}}
										{...section.slot_props.props}
									/>
								</SceneSection>
							{/each}
						</div>
					{/key}
				{:else if activeTab === 1}
					<div class="flex-full-height" />

					<ControlScenePlay item={scene} variant="drawer" />
					<ControlSceneEnable item={scene} variant="drawer" />
				{:else if activeTab === 2}
					<ControlsContainer class="mt-m" size="s">
						<svelte:fragment slot="first">
							<Text
								styleType="body-small"
								class="text-bold align-self-center text-capitalize text-left"
							>
								{$format('scene_id', { default: 'Scene id' })}
							</Text>
							<hr class="mt-s mb-s" />
							<div class="d-flex justify-content-between align-items-center">
								<Text class="text-break-all" styleType="body-small">{id}</Text>

								<IconButton
									aria-label={$format('button_copy_scene_id', {
										default: 'Button copy scene id',
									})}
									class="ml-s"
									icon="fas fa-copy"
									on:click={() =>
										copy(
											id,
											$format('copied_successfully', {
												default: 'Copied successfully!',
											}),
										)}
								/>
							</div>
							{#if isDevServerUser(['repo', 'nb']) || isT1User()}
								<hr class="mt-s mb-s border-color-base" />
								<div class="d-flex justify-content-between align-items-center">
									<Button
										on:click={() => {
											showCodePreviewModal = true;
										}}
										width="full"
										size="m"
										variant="secondary"
									>
										{$format('button_open_scene_code', {
											default: 'open scene JSON',
										})}
									</Button>
								</div>
							{/if}
						</svelte:fragment>
					</ControlsContainer>
				{:else if activeTab === 3}
					<ScriptSceneSection
						user_data={$user_data}
						{scene}
						activeTimeComponent={ActiveTimePreview}
						morePreviewComponent={MorePreview}
						on:sceneConvertedToScript={(e) => onSceneConvertedToScript(e)}
					/>
				{/if}
			</DrawerTabContent>
		{:else}
			<DrawerTabContent heading={$format('title_deleted_scene', { default: 'Deleted scene' })}>
				<div class="flex-full-height" />
				<ParameterCard
					icon="fas fa-film"
					title={$format('label_not_found_el', {
						default: 'Not found element',
					})}
					description={'ID:' + id}
					size="m"
				/>
			</DrawerTabContent>
		{/if}
	{/key}
</DrawerTabs>

{#if showAddEditModal}
	<SceneWizard bind:id bind:showAddEditModal scene_object={scene} />
{/if}

{#if showAddEditCondition}
	<WhenStep
		backdropCloseModal
		{element}
		{when_info}
		hideWhens
		saveEditSteps
		bind:scene_object={scene}
		bind:showAddEditCondition
		on:save={handleSave}
	/>
{/if}

{#if showAddEditAction}
	<DoStep
		backdropCloseModal
		on:save={handleSave}
		{element}
		{do_info}
		bind:showAddEditAction
		hideActions
		bind:scene_object={scene}
	/>
{/if}

{#if showEditActiveTime}
	<ActiveTimeStep
		backdropCloseModal
		on:save={handleSave}
		hideInterval
		bind:showEditActiveTime
		{editedInterval}
		bind:scene_object={scene}
	/>
{/if}

{#if isDevServerUser(['repo', 'nb']) || isT1User()}
	<Modal
		on:close={onCloseCodePreviewModal}
		showModal={showCodePreviewModal}
		title={$format('modal_title_scene_code', {
			default: 'Scene JSON',
		})}
	>
		<svelte:fragment slot="content">
			<Container hideSlotBottom hideSlotTop padding="0">
				<ScriptsHighlight code={JSON.stringify(scene, null, 2)} />
			</Container>
		</svelte:fragment>
		<div slot="footer" class="d-flex justify-content-end">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				variant="secondary"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={(e) => (showCodePreviewModal = false)}
			>
				{capFirstChar($format('button_close', { default: 'Close' }))}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="primary"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={(e) => {
					copy(
						JSON.stringify(scene, null, 2),
						$format('copied_successfully', {
							default: 'Copied successfully!',
						}),
					);
					showCodePreviewModal = false;
				}}
			>
				{capFirstChar($format('button_copy', { default: 'Copy' }))}
			</Button>
		</div>
	</Modal>
{/if}

<!-- Duplicate Scene Confirmation Modal -->
<Modal on:close={onCloseDuplicateConfirmModal} showModal={showDuplicateConfirmModal} hideSlotToolbar>
	<svelte:fragment slot="content">
		<Container hideSlotBottom hideSlotToolbar="true" padding="0">
			<div class="mb-s d-flex justify-content-center">
				<Icon icon="fa-light fa-triangle-exclamation text-warning" size="xxl" />
			</div>
			<div class="p-s">
				<Text styleType="body" class="mb-m text-center">
					{$format('modal_duplicate_scene_confirm', {
						default: 'Are you sure you want to duplicate this scene?',
					})}
				</Text>
				<Text styleType="body-small" class="text-color-muted text-center">
					{$format('modal_duplicate_scene_description', {
						default: 'A copy of the scene will be created and will be inactive by default.',
						values: { name: scene?._meta?.name || 'Scene' },
					})}
				</Text>
			</div>
		</Container>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-center">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={onCloseDuplicateConfirmModal}
		>
			{capFirstChar($format('button_cancel', { default: 'Cancel' }))}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			variant="primary"
			responsiveSize={{
				sm: 'm',
			}}
			disabled={isDuplicating}
			aria-label={$format('button_duplicate_scene', { default: 'Duplicate scene' })}
			on:click={confirmDuplicateScene}
		>
			{#if isDuplicating}
				{capFirstChar($format('duplicate_label', { default: 'Duplicating...' }))}
			{:else}
				{capFirstChar($format('duplicate_label', { default: 'Duplicate' }))}
			{/if}
		</Button>
	</div>
</Modal>

<!-- Post Duplicate Modal -->
<Modal
	on:close={onClosePostDuplicateModal}
	showModal={showPostDuplicateModal}
	title={$format('success_duplicate_scene', {
		default: 'Scene Duplicated',
	})}
>
	<svelte:fragment slot="content">
		<Container hideSlotBottom hideSlotTop padding="0">
			<div class="p-m">
				<Text styleType="body" class="mb-m">
					{$format('modal_scene_duplicated_edit_question', {
						default: 'Would you like to edit the newly created scene now?',
					})}
				</Text>
			</div>
		</Container>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={onClosePostDuplicateModal}
		>
			{capFirstChar($format('button_no', { default: 'No' }))}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			variant="primary"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={handleEditDuplicatedScene}
		>
			{capFirstChar($format('button_yes', { default: 'Yes' }))}
		</Button>
	</div>
</Modal>

{#if scene?.infinno}
	<Modal
		type="fullScreen"
		backdropClose={false}
		showModal={showScriptEditorModal}
		on:close={() => onScriptEditorClose()}
		title="Script scene editor"
	>
		<ScriptSceneEditor
			slot="content"
			user_data={$user_data}
			{scene}
			activeTimeCalculationHelpers={{
				_calculate_duration,
				calculateWhenEnd,
			}}
			activeTimeComponent={AddEditInterval}
			ScriptEditorClass={ShellyScriptEditor}
		/>
	</Modal>
{/if}

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context="edit_entity" />
